import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, SERVICES } from "@/lib/data";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="dark-island mt-24">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 pt-20 pb-10">
        {/* Big CTA row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 border-b border-on-dark">
          <div className="lg:col-span-7">
            <p className="overline mb-6">Let's get to work</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02] text-white">
              One call. <br />
              Every trade you need.
            </h2>
            <p className="mt-6 text-base muted-on-dark max-w-xl">
              Tell us what's on your list. We'll come back with a fixed quote and a clear timeline — usually within one business day.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end lg:justify-end">
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="footer-call-cta"
              className="group inline-flex items-center justify-between gap-6 bg-accent hover:bg-accent/90 text-white px-6 py-5 w-full lg:w-auto min-w-[300px] transition-colors"
            >
              <span className="flex flex-col items-start">
                <span className="text-[10px] tracking-[0.28em] uppercase opacity-80">Call now</span>
                <span className="font-display text-2xl font-bold">{PHONE_DISPLAY}</span>
              </span>
              <Phone className="w-5 h-5" />
            </a>
            <Link
              to="/contact"
              data-testid="footer-quote-cta"
              className="group inline-flex items-center justify-between gap-6 border border-on-dark hover:border-white/60 text-white px-6 py-5 w-full lg:w-auto min-w-[300px] transition-colors"
            >
              <span className="flex flex-col items-start">
                <span className="text-[10px] tracking-[0.28em] uppercase opacity-70">Or</span>
                <span className="font-display text-2xl font-bold">Get a free quote</span>
              </span>
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        {/* Detail rows */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10 py-12">
          <div className="col-span-2 md:col-span-4">
            <div className="font-display text-xl font-extrabold text-white">RSR Australia</div>
            <p className="mt-3 text-sm muted-on-dark max-w-sm">
              Property maintenance, repairs and trades for residential and commercial clients across Australia.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 text-white hover:text-accent transition-colors">
                <Phone className="w-3.5 h-3.5" /> {PHONE_DISPLAY}
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-3 text-white hover:text-accent transition-colors">
                <Mail className="w-3.5 h-3.5" /> {EMAIL}
              </a>
              <div className="flex items-center gap-3 muted-on-dark">
                <MapPin className="w-3.5 h-3.5" /> Servicing Australia-wide
              </div>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="overline">Services</div>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              {SERVICES.map(s => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-white/80 hover:text-accent transition-colors"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline">Company</div>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/homeowners" className="text-white/80 hover:text-accent">For homeowners</Link></li>
              <li><Link to="/commercial" className="text-white/80 hover:text-accent">For business</Link></li>
              <li><Link to="/pricing" className="text-white/80 hover:text-accent">Pricing & quotes</Link></li>
              <li><Link to="/emergency" className="text-white/80 hover:text-accent">Emergency repairs</Link></li>
              <li><Link to="/about" className="text-white/80 hover:text-accent">About</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-accent">Contact</Link></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-on-dark flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs muted-on-dark">
          <div>© {year} RSR Australia. All rights reserved.</div>
          <div className="flex items-center gap-3">
            <span>Fully insured</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>Licensed trades</span>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <span>ABN registered</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
